import React, { useContext } from 'react';

import PropTypes from 'prop-types';
import { Header, Tab } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import HelpTooltip from 'components/common/HelpTooltip';
import { PageContentContext } from 'components/modules/content/context';
import { ContentSuggestionType } from 'components/modules/content/types';
import { PageComponentType, PageComponentSettingType } from 'components/modules/pages/types';
import { isAdminUser } from 'libs/auth';
import { selectBusiness } from 'selectors';
import { selectUser } from 'selectors/user';

import { generateHelpText } from './PageContentNode.utils';
import CustomCtasConfiguration from './components/CustomCtasConfiguration';
import DefaultValueNotice from './components/DefaultValueNotice';
import SuggestionsButton from './components/SuggestionsButton';
import ContentNodeForm from '../ContentNodeForm';

import './PageContentNode.scss';

const propTypes = {
  component: PageComponentType.isRequired,
  setting: PageComponentSettingType.isRequired,
  suggestions: PropTypes.arrayOf(ContentSuggestionType),
  onCancel: PropTypes.func,
};

const defaultProps = {
  suggestions: [],
  onCancel: () => null,
};

export default function PageContentNode({ component, setting, suggestions, onCancel }) {
  const business = useSelector(selectBusiness);
  const hasIntelligentWebsiteProduct = _get(business, 'hasIntelligentWebsiteProduct', false);

  const { refreshPages, onSaveContentNode: onSaveContentNodeContext } =
    useContext(PageContentContext);

  const {
    displayName,
    helpText = '',
    contentApplicationOptions = {},
    allowCustomCtas,
  } = setting || {};
  const { default: defaultValue = '' } = contentApplicationOptions || {};
  const { contentNodes = [], id: componentConfigurationId } = component;
  const isDefaultValue = contentNodes.every((node) => node.content === defaultValue);

  const user = useSelector(selectUser);
  const isAdmin = isAdminUser(user);

  async function onSaveContentNode(contentNode, newContent) {
    await onSaveContentNodeContext(contentNode, newContent, { component });
  }

  async function onReviewSuggestions() {
    await refreshPages();
  }

  function getDisplayName(settingDisplayName, nodes) {
    if (settingDisplayName) {
      return settingDisplayName;
    }

    if (!_isEmpty(nodes)) {
      return (contentNodes[0].contentName || '').replace(/_/g, ' ');
    }

    return '';
  }

  const finalHelpText = generateHelpText(helpText, defaultValue);
  const contentNode = contentNodes[0];

  const displaySuggestionButton =
    !_isEmpty(suggestions) && (isAdmin || hasIntelligentWebsiteProduct);

  const showInteractionsTab = isAdmin && allowCustomCtas;

  const panes = [
    {
      menuItem: 'Content',
      render: () => (
        <Tab.Pane attached={false} key="content">
          {contentNodes.map((node) => (
            <ContentNodeForm
              key={node.id}
              contentNode={node}
              componentConfigurationId={componentConfigurationId}
              setting={setting}
              onCancel={onCancel}
              onSave={onSaveContentNode}
            />
          ))}
        </Tab.Pane>
      ),
    },
  ];

  if (showInteractionsTab) {
    panes.push({
      menuItem: 'Interactions',
      render: () => (
        <Tab.Pane attached={false} key="interactions">
          <CustomCtasConfiguration component={component} />
        </Tab.Pane>
      ),
    });
  }

  return (
    <div className="page-content-node">
      <div className="header-section">
        <Header as="h3" className="section-header">
          {getDisplayName(displayName, contentNodes)}
        </Header>
        {!_isEmpty(finalHelpText) && (
          <HelpTooltip title={displayName} description={finalHelpText} position="bottom left" />
        )}
        {isDefaultValue && isAdmin && <DefaultValueNotice />}
        {displaySuggestionButton && (
          <SuggestionsButton
            suggestions={suggestions}
            contentNode={contentNode}
            onReviewSuggestions={onReviewSuggestions}
          />
        )}
      </div>

      <Tab
        className="page-content-node-tabs"
        menu={{ secondary: true, pointing: true }}
        panes={panes}
      />
    </div>
  );
}

PageContentNode.propTypes = propTypes;
PageContentNode.defaultProps = defaultProps;
